<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const isSubmitting = ref(false);
const showModal = ref(false);
const editing = ref(null);
const lastSavedFixedSnapshot = ref('');

const storeLinks = computed(() => store.getters['aiCloser/getLinks']);

const links = reactive([]);

const linkTypeOptions = computed(() => [
  {
    value: 'signup',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.SIGNUP'),
  },
  {
    value: 'promo',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.PROMO'),
  },
  {
    value: 'entry',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.ENTRY'),
  },
  {
    value: 'deposit',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.DEPOSIT'),
  },
  {
    value: 'withdraw',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.WITHDRAW'),
  },
  {
    value: 'support',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.SUPPORT'),
  },
  {
    value: 'event',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.EVENT'),
  },
  {
    value: 'other',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.TYPES.OTHER'),
  },
]);

const linkTypeLabel = type => {
  return linkTypeOptions.value.find(opt => opt.value === type)?.label || type;
};

const fixedTypes = ['signup', 'promo'];

const newItem = reactive({
  link_type: 'event',
  title: '',
  url: '',
  use_case: '',
  country: 'TH',
  language: 'th',
  priority: 100,
  enabled: true,
});

const fetchLinks = async () => {
  if (!props.assistantId) return;
  isLoading.value = true;
  try {
    await store.dispatch('aiCloser/fetchLinks', {
      assistantId: props.assistantId,
      params: { limit: 200, offset: 0 },
    });
  } catch (e) {
    // ignore
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchLinks);

watch(
  () => storeLinks.value,
  newVal => {
    links.length = 0;
    (newVal || []).forEach(l => links.push(l));
  },
  { immediate: true, deep: true }
);

const fixedLinkByType = type => {
  return (links || []).find(l => l.link_type === type) || null;
};

const snapshotFixedLinks = () =>
  JSON.stringify({
    signup: (signupUrlInput.value || '').trim(),
    promo: (promoUrlInput.value || '').trim(),
  });

const syncFixedLinkInputs = () => {
  signupUrlInput.value = fixedLinkByType('signup')?.url || '';
  promoUrlInput.value = fixedLinkByType('promo')?.url || '';
  lastSavedFixedSnapshot.value = snapshotFixedLinks();
};

const openAdd = () => {
  editing.value = null;
  Object.assign(newItem, {
    link_type: 'event',
    title: '',
    url: '',
    use_case: '',
    country: 'TH',
    language: 'th',
    priority: 100,
    enabled: true,
  });
  showModal.value = true;
};

const openEdit = item => {
  editing.value = item;
  Object.assign(newItem, {
    link_type: item.link_type || 'other',
    title: item.title || '',
    url: item.url || '',
    use_case: item.use_case || '',
    country: item.country || 'TH',
    language: (item.language || 'th').toLowerCase(),
    priority: Number(item.priority ?? 100),
    enabled: item.enabled !== false,
  });
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const upsertFixed = async (type, url) => {
  const existing = fixedLinkByType(type);
  const payload = {
    link_type: type,
    title: type === 'signup' ? linkTypeLabel('signup') : linkTypeLabel('promo'),
    url: (url || '').trim(),
    country: 'TH',
    language: 'th',
    priority: 1,
    enabled: true,
  };
  if (!payload.url) return;

  if (existing?.id) {
    await store.dispatch('aiCloser/updateLink', {
      assistantId: props.assistantId,
      linkId: existing.id,
      data: payload,
    });
  } else {
    await store.dispatch('aiCloser/createLink', {
      assistantId: props.assistantId,
      data: payload,
    });
  }
};

const handleSaveFixed = async () => {
  if (!props.assistantId) return;
  isSubmitting.value = true;
  try {
    const signupUrl = (signupUrlInput.value || '').trim();
    const promoUrl = (promoUrlInput.value || '').trim();
    if (signupUrl) await upsertFixed('signup', signupUrl);
    if (promoUrl) await upsertFixed('promo', promoUrl);
    lastSavedFixedSnapshot.value = snapshotFixedLinks();
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.SAVED'));
    await fetchLinks();
  } catch (e) {
    useAlert(
      e?.message || t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const signupUrlInput = ref('');
const promoUrlInput = ref('');
const canSaveFixed = computed(
  () =>
    Boolean(
      props.assistantId &&
        !isLoading.value &&
        !isSubmitting.value &&
        !showModal.value &&
        snapshotFixedLinks() !== lastSavedFixedSnapshot.value
    )
);

watch(
  () => links,
  () => {
    syncFixedLinkInputs();
  },
  { deep: true, immediate: true }
);

const additionalLinks = computed(() => {
  return (links || [])
    .filter(l => !fixedTypes.includes(l.link_type))
    .slice()
    .sort(
      (a, b) =>
        Number(a.priority || 100) - Number(b.priority || 100) || b.id - a.id
    );
});

const handleDelete = async item => {
  if (!props.assistantId || !item?.id) return;
  if (!confirm(t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.DELETE_CONFIRM')))
    return;
  try {
    await store.dispatch('aiCloser/deleteLink', {
      assistantId: props.assistantId,
      linkId: item.id,
    });
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.DELETED'));
  } catch (e) {
    useAlert(
      e?.message || t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.DELETE_FAILED')
    );
  }
};

const handleSaveModal = async () => {
  if (!props.assistantId) return;
  if (!newItem.url?.trim()) {
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.URL_REQUIRED'));
    return;
  }
  isSubmitting.value = true;
  try {
    const payload = {
      link_type: newItem.link_type,
      title: newItem.title,
      url: newItem.url,
      use_case: newItem.use_case,
      country: newItem.country,
      language: (newItem.language || 'th').toLowerCase(),
      priority: Number(newItem.priority ?? 100),
      enabled: newItem.enabled !== false,
    };
    if (editing.value?.id) {
      await store.dispatch('aiCloser/updateLink', {
        assistantId: props.assistantId,
        linkId: editing.value.id,
        data: payload,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.UPDATED'));
    } else {
      await store.dispatch('aiCloser/createLink', {
        assistantId: props.assistantId,
        data: payload,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.ADDED'));
    }
    showModal.value = false;
    await fetchLinks();
  } catch (e) {
    useAlert(
      e?.message || t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({
  save: handleSaveFixed,
  canSave: () => canSaveFixed.value,
});
</script>

<template>
  <div class="links-tab">
    <div class="guide">
      <div class="guide-title">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.GUIDE.TITLE') }}
      </div>
      <ul class="guide-list">
        <li>
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.GUIDE.ITEMS.USE_FOR') }}
        </li>
        <li>
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.GUIDE.ITEMS.AI_USAGE') }}
        </li>
      </ul>
    </div>

    <div class="card">
      <div class="card-title">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FIXED.TITLE') }}
      </div>
      <div class="grid2">
        <div class="field">
          <label>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FIXED.SIGNUP_LABEL')
          }}</label>
          <input
            v-model="signupUrlInput"
            class="input"
            placeholder="https://..."
          />
        </div>
        <div class="field">
          <label>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FIXED.PROMO_LABEL')
          }}</label>
          <input
            v-model="promoUrlInput"
            class="input"
            placeholder="https://..."
          />
        </div>
      </div>
      <div class="actions">
        <button
          class="btn primary"
          type="button"
          :disabled="!canSaveFixed"
          @click="handleSaveFixed"
        >
          <span
            v-if="isSubmitting"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ACTIONS.SAVE_LINKS') }}
        </button>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <div class="card-title">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ADDITIONAL.TITLE') }}
        </div>
        <button class="btn" type="button" @click="openAdd">
          <span class="i-lucide-plus size-4" />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ACTIONS.ADD_LINK') }}
        </button>
      </div>

      <div v-if="isLoading" class="empty">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ADDITIONAL.LOADING') }}
      </div>
      <div v-else-if="additionalLinks.length === 0" class="empty">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.ADDITIONAL.EMPTY') }}
      </div>
      <div v-else class="list">
        <div v-for="item in additionalLinks" :key="item.id" class="row">
          <div class="row-main">
            <div class="row-title">
              <span class="badge">{{ linkTypeLabel(item.link_type) }}</span>
              <span class="title">{{ item.title || item.url }}</span>
              <span v-if="item.enabled === false" class="badge off">{{
                t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.STATUS.DISABLED')
              }}</span>
            </div>
            <div class="row-sub">
              <span class="url">{{ item.url }}</span>
              <span v-if="item.use_case" class="usecase"
                >• {{ item.use_case }}</span
              >
            </div>
          </div>
          <div class="row-actions">
            <button class="icon-btn" type="button" @click="openEdit(item)">
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              class="icon-btn danger"
              type="button"
              @click="handleDelete(item)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <div class="modal-header">
            <h3>
              {{
                editing
                  ? t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.MODAL.EDIT_TITLE')
                  : t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.MODAL.ADD_TITLE')
              }}
            </h3>
            <button class="icon-btn" type="button" @click="closeModal">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="modal-body">
            <div class="grid2">
              <div class="field">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.TYPE')
                }}</label>
                <select v-model="newItem.link_type" class="input">
                  <option
                    v-for="opt in linkTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="field">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.PRIORITY')
                }}</label>
                <input
                  v-model="newItem.priority"
                  class="input"
                  :placeholder="
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.PLACEHOLDERS.PRIORITY'
                    )
                  "
                />
              </div>
            </div>

            <div class="field">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.TITLE')
              }}</label>
              <input
                v-model="newItem.title"
                class="input"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.PLACEHOLDERS.TITLE'
                  )
                "
              />
            </div>
            <div class="field">
              <label
                >{{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.URL') }} *</label
              >
              <input
                v-model="newItem.url"
                class="input"
                placeholder="https://..."
              />
            </div>
            <div class="field">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.USE_CASE')
              }}</label>
              <textarea
                v-model="newItem.use_case"
                class="textarea"
                rows="3"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.PLACEHOLDERS.USE_CASE'
                  )
                "
              />
            </div>
            <div class="grid2">
              <div class="field">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.COUNTRY')
                }}</label>
                <input
                  v-model="newItem.country"
                  class="input"
                  placeholder="TH"
                />
              </div>
              <div class="field">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.LANGUAGE')
                }}</label>
                <input
                  v-model="newItem.language"
                  class="input"
                  placeholder="th"
                />
              </div>
            </div>
            <div class="field">
              <label class="check">
                <input v-model="newItem.enabled" type="checkbox" />
                {{ t('AILAB.ASSISTANT_V2.PRODUCT_LINKS.FORM.ENABLED') }}
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn" type="button" @click="closeModal">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button
              class="btn primary"
              type="button"
              :disabled="isSubmitting"
              @click="handleSaveModal"
            >
              <span
                v-if="isSubmitting"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              {{ t('AILAB.FORM.SAVE') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.links-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.guide {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 14px 16px;
}
.guide-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  margin-bottom: 8px;
}
.guide-list {
  margin: 0;
  padding-left: 18px;
  color: var(--wlab-text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

.card {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 14px 16px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.card-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  margin-bottom: 10px;
}
.card-header .card-title {
  margin-bottom: 0;
}

.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--wlab-text-secondary);
}
.input,
.textarea {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
}
.textarea {
  resize: vertical;
}
.check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--wlab-text-secondary);
}

.actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 10px;
  color: var(--wlab-text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.btn.primary {
  background: #ffffff;
  border: none;
  color: var(--wlab-text-primary);
}

.list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--wlab-border-light);
  background: var(--wlab-bg-tertiary);
  border-radius: 12px;
}
.row-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: var(--wlab-text-primary);
}
.badge {
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.18);
  color: #60a5fa;
}
.badge.off {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.18);
  color: #ef4444;
}
.row-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--wlab-text-muted);
}
.url {
  word-break: break-all;
}
.row-actions {
  display: flex;
  gap: 8px;
}
.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--wlab-border-default);
  background: transparent;
  color: var(--wlab-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-btn.danger {
  border-color: rgba(239, 68, 68, 0.35);
  color: #ef4444;
}

.empty {
  margin-top: 12px;
  color: var(--wlab-text-muted);
  font-size: 13px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
}
.modal {
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  background: var(--wlab-bg-primary);
  border: 1px solid var(--wlab-border-default);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--wlab-border-light);
}
.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--wlab-text-primary);
}
.modal-body {
  padding: 16px 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid var(--wlab-border-light);
}
</style>
